import {Injectable} from "@angular/core";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import {Constants} from "_@shared/models/constants";
import Color from "@arcgis/core/Color";
import {ServiceCenterStatus} from "_@api/api.service";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import {FieldProperties} from "@arcgis/core/layers/support/Field";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Font from "@arcgis/core/symbols/Font";

const BLACK_COLOR = [0, 0, 0, 1];

const SIMPLE_LINE = new SimpleLineSymbol({
  style: "solid",
  color: BLACK_COLOR,
  width: 1
});

const BLACK_SYMBOL = new SimpleMarkerSymbol({
  style: "circle",
  size: 6,
  outline: SIMPLE_LINE,
  color: BLACK_COLOR
});

const RED_SYMBOL = new SimpleMarkerSymbol({
  style: "circle",
  size: 6,
  outline: SIMPLE_LINE,
  color: new Color("#CC0000")
});

const ORANGE_SYMBOL = new SimpleMarkerSymbol({
  style: "circle",
  size: 6,
  outline: SIMPLE_LINE,
  color: new Color("#E56717")
});

const YELLOW_SYMBOL = new SimpleMarkerSymbol({
  style: "circle",
  size: 6,
  outline: SIMPLE_LINE,
  color: new Color("#FFCA5F")
});

const BLUE_SYMBOL = new SimpleMarkerSymbol({
  style: "circle",
  size: 6,
  outline: SIMPLE_LINE,
  color: new Color("#0247FE")
});

const GREEN_SYMBOL = new SimpleMarkerSymbol({
  style: "circle",
  size: 6,
  outline: SIMPLE_LINE,
  color: new Color("#009933")
});

const TEXT_BY_COLOR = {
  RED: "% - Extremely limited operations with major service delays",
  ORANGE: "% - Limited operations with expected service delays",
  YELLOW: "% - Moderate service delays",
  BLUE: "% - Slight service delays in select service areas"
};

const SYMBOL_BY_COLOR = {
  RED: RED_SYMBOL,
  ORANGE: ORANGE_SYMBOL,
  YELLOW: YELLOW_SYMBOL,
  BLUE: BLUE_SYMBOL
};

@Injectable({
  providedIn: 'root'
})
export class ServiceCenterService {
  
  public featureLayerFields: FieldProperties[] = [
    {name: "id", type: "string"},
    {name: "sca", type: "string"},
    {name: "cat", type: "string"},
    {name: "date", type: "string"},
    {name: "notes", type: "string"},
    {name: "percent", type: "string"},
    {name: "name", type: "string"},
    {name: "color", type: "string"},
    {name: "closed", type: "string"},
    {name: "text", type: "string"}
  ];
  
  async loadLayer(serviceCentersStatus: ServiceCenterStatus[]): Promise<GraphicsLayer[]> {
    let statusOKSVCs = new GraphicsLayer({id: Constants.SERVICE_CENTERS_STATUS_OK_LAYER_ID});
    let statusWaringSVCs = new GraphicsLayer({id: Constants.SERVICE_CENTERS_STATUS_WARNINGS_LAYER_ID});
    
    serviceCentersStatus?.forEach(item => {
      const geometry = new Point({
        longitude: Number.parseFloat(String(item.longitude)),
        latitude: Number.parseFloat(String(item.latitude))
      });
      
      const labelGraphic = this.getLabelGraphic(item, geometry);
      
      if (item.color === Constants.GREEN) {
        const g = this.makeGraphic(geometry, GREEN_SYMBOL, item);
        statusOKSVCs.addMany([g, labelGraphic]);
      } else if (item.closed === "CLOSED") {
        const attrs = { ...item, percent: "", text: "Closed" };
        const g = this.makeGraphic(geometry, BLACK_SYMBOL, attrs);
        statusWaringSVCs.addMany([g, labelGraphic]);
      } else {
        const symbol = SYMBOL_BY_COLOR[item.color];
        if (symbol) {
          const attrs = { ...item, text: TEXT_BY_COLOR[item.color] };
          const g = this.makeGraphic(geometry, symbol, attrs);
          statusWaringSVCs.addMany([g, labelGraphic]);
        }
      }
    })
    
    return [statusOKSVCs, statusWaringSVCs];
  }
  
  private makeGraphic(geometry: Point, symbol: SimpleMarkerSymbol | TextSymbol, attrs?: ServiceCenterStatus) {
    return new Graphic({
      geometry,
      symbol,
      attributes: attrs
    });
  }
  
  private getLabelGraphic(item: ServiceCenterStatus, geometry: Point) {
    const font = new Font({
      size: 7,
      weight: 'normal',
      family: 'Verdana'
    });

    const textSymbol = new TextSymbol({
      text: item.color === Constants.GREEN ? item.sca : item.sca + ' ' + item.percent + '%',
      color: BLACK_COLOR,
      borderLineColor: '#FFFFFF',
      font: font,
      angle: 0,
      verticalAlignment: 'middle',
      horizontalAlignment: 'center',
      xoffset: 0,
      yoffset: -8,
    });
    
    return this.makeGraphic(geometry, textSymbol);
  }
}
